import { TrackingEvent, sendTrackingEvent } from '../services/service.tracking';
import { state as aftermarketState } from '../store/store.aftermarket';
import { state as exteriorState } from '../store/store.exterior';
import { state as imageCaptureState } from '../store/store.image-capture';
import { state as interiorState } from '../store/store.interior';
import { state as mechanicalState } from '../store/store.mechanical';
import { getModificationsResponse } from '../utils/utils.answers';
import { getVehicleQuestionData } from '../utils/utils.tracking';

export const tabToEventElement = {
  exterior: 'ico_condition_ext',
  interior: 'ico_condition_int',
  mechanical: 'ico_condition_mech',
};

const issueClickEventTags = {
  questionsEngine: 'button::subnav::engine',
  questionsTransmission: 'button::subnav::transmission',
  questionsSteering: 'button::subnav::steering',
  questionsExhaust: 'button::subnav::exhaust',
  questionsSuspension: 'button::subnav::suspension',
  questionsHeatingAir: 'button::subnav::heating_air_conditioning',
  questionsBatteryElectrical: 'button::subnav::battery_electrical',
  questionsStereo: 'button::subnav::stereo',
  questionsOdometer: 'button::subnav::odometer',
  questionsSunMoonRoof: 'button::subnav::sunroof_moonroof',
  questionsAirbags: 'button::subnav::airbags',
  questionsFrameStructural: 'button::subnav::frame_structural',
};

export { issueClickEventTags as MECHANICAL_TAGS };

const tabToEventResult = {
  sch: 'seats_carpet_headliner',
  'dash-lights': 'dashboard_lights',
  windowsLights: 'windows_lights',
};

export const trackConditionTabSelection = (tab: string, previousTab?: string) => {
  const eventResult = tabToEventElement[tab];

  if (eventResult) {
    sendTrackingEvent({
      event: 'analytics_event',
      eventAction: 'impression',
      eventResult,
      eventNonInteractive: false,
      eventData: { ...getVehicleQuestionData(), previousTab },
    });
  }
};

export const trackExteriorClick = (previousTab?: string) => {
  sendTrackingEvent({
    event: 'analytics_event',
    eventAction: 'click',
    eventResult: 'button::nav::exterior',
    eventNonInteractive: false,
    eventData: { ...getVehicleQuestionData(), previousTab },
  });
};

export const trackConditionSubNavClick = (button: string) => {
  try {
    sendTrackingEvent({
      event: 'analytics_event',
      eventAction: 'click',
      eventResult: issueClickEventTags[button],
      eventNonInteractive: false,
      eventData: getVehicleQuestionData(),
    });
  } catch (error) {
    console.log('ERROR: SubNav TrackingEvent', error);
  }
};

export const trackExteriorTabSelection = (tab: string) => {
  const tabLabel = tabToEventResult[tab] ?? tab;

  sendTrackingEvent({
    event: 'analytics_event',
    eventAction: 'click',
    eventResult: `button::subnav::${tabLabel}`,
    eventNonInteractive: false,
    eventData: getVehicleQuestionData(),
  });
};

export const trackInteriorTabSelection = (tab: string) => {
  const tabLabel = tabToEventResult[tab] ?? tab;

  sendTrackingEvent({
    event: 'analytics_event',
    eventAction: 'click',
    eventResult: `button::subnav::${tabLabel}`,
    eventNonInteractive: false,
    eventData: getVehicleQuestionData(),
  });
};

export const trackOpenModal = () => {
  sendTrackingEvent({
    event: 'analytics_event',
    eventAction: 'impression',
    eventResult: 'conditions-overlay::open',
    eventNonInteractive: false,
  });
};

export const trackNextButton = (stepName: string) => {
  const event: TrackingEvent = {
    event: 'analytics_event',
    eventAction: 'click',
    eventResult: `cta::continue`,
    eventNonInteractive: false,
  };

  if (stepName.startsWith('image-capture')) {
    event.eventData = getImageCaptureResponseData(imageCaptureState);
  } else if (stepName === 'vehicle-conditions') {
    event.eventData = getIssuesResponseData({ exteriorState, interiorState, mechanicalState });
  } else if (stepName === 'keys-modifications-form') {
    event.eventData = getModificationsResponse(aftermarketState);
  }

  sendTrackingEvent(event);
};

export const sendModificationResponses = () => {
  sendTrackingEvent({
    event: 'analytics_event',
    eventAction: 'click',
    eventResult: `ico responses`,
    eventNonInteractive: false,
    eventData: getModificationsResponse(aftermarketState),
  });
};

const getImageCaptureResponseData = (state: typeof imageCaptureState): object => {
  const urls = state.imageUrls;
  return {
    icoResponses: {
      responseFields: urls.map((_url: string, index: number) => `pic${index + 1}`).join('::'),
      responseValues: urls.map((url: string) => (url ? 'yes' : 'no')).join('::'),
    },
  };
};

export const getIssuesResponseData = ({ exteriorState: ext, interiorState: int, mechanicalState: mech }) => {
  const exterior = [ext.conditionPageFrontBadge, ext.conditionPageDriverBadge, ext.conditionPageRearBadge, ext.conditionPagePassengerBadge, ext.conditionPageWindowsLightsBadge];
  const interior = [int.conditionPageSche, int.conditionPageDbl];
  const responseValues = [...exterior, ...interior, ...mech.subIssueCounts].map((count: number) => (count > 0 ? 'yes' : 'no')).join('::');
  return {
    icoResponses: {
      responseKeys:
        'front::driver::rear::passenger::wheels_lights::seat_carpet_headliner::dashboard-lights::enginemech::transmech::steermech::exhaustmech::suspenmech::heatairmech::battmech::stereomech::odommech::sunroofmech::airbagmech::framemech',
      responseValues,
    },
  };
};

export const trackBackButton = () => {
  sendTrackingEvent({
    event: 'analytics_event',
    eventAction: 'click',
    eventResult: `cta::back`,
    eventNonInteractive: false,
  });
};

export const trackSubmitButton = () => {
  sendTrackingEvent({
    event: 'analytics_event',
    eventAction: 'click',
    eventResult: 'submit::ico::get-my-offer',
    eventNonInteractive: false,
  });
};
export const trackConditionTierModalCancel = () => {
  sendTrackingEvent({
    event: 'analytics_event',
    eventAction: 'click',
    eventResult: `text-link::conditions-overlay::cancel`,
    eventNonInteractive: false,
  });
};

export const trackConditionTierModalConfirm = () => {
  sendTrackingEvent({
    event: 'analytics_event',
    eventAction: 'click',
    eventResult: `text-link::conditions-overlay::next`,
    eventNonInteractive: false,
  });
};

export const trackYesItsPerfectModal = (action: string) => {
  sendTrackingEvent({
    event: 'analytics_event',
    eventAction: 'click',
    eventResult: `cta::modal::${action}`,
    eventNonInteractive: false,
  });
};
